import cn from 'classnames';
import { differenceInDays, format, isBefore, isSameDay } from 'date-fns';
import { noop } from 'lodash';
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DateFormatsEnum } from '../../../../constants/date';
import useModal from '../../../../hooks/useModal';
import { IDatePickerOutput, IDatePickerRef, UISizeEnum } from '../../../../types/shell';
import { IFiltersRef } from '../../../../types/tables';
import { getUTCDate } from '../../../../utils/date';
import { ActionButton, ControlButton, DropdownWithContent } from '../../../index';
import DatePicker from '../DatePicker/DatePicker';

import styles from './DateRangePicker.module.scss';

interface IProps {
  value: IDatePickerOutput | null;
  maxDate?: Date;
  wide?: boolean;
  openUpward?: boolean;
  pickerClassName?: string;
  onReset?: () => void;
  onSelect?: (value: IDatePickerOutput) => void;
  labelClassName?: string;
  muiArrow?: boolean;
  aboveDialog?: boolean;
  filtersRef?: ForwardedRef<IFiltersRef>;
}

interface IControlProps {
  close: () => void;
}

const DateRangePicker = forwardRef<IDatePickerRef, IProps>(
  (
    {
      value,
      maxDate,
      onReset,
      onSelect,
      wide,
      openUpward,
      pickerClassName,
      labelClassName,
      muiArrow = false,
      aboveDialog = false,
    }: IProps,
    ref,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { from, to } = useMemo(() => value || ({} as IDatePickerOutput), [value]);
    const maximumDate = useMemo(
      () => (maxDate ? getUTCDate(maxDate.toISOString()) : getUTCDate(new Date().toISOString())),
      [maxDate],
    );

    const [startDate, setStartDate] = useState<Date | null>(from ? getUTCDate(from) : null);
    const [endDate, setEndDate] = useState<Date | null>(to ? getUTCDate(to) : null);

    const startDateRef = useRef(startDate);
    const endDateRef = useRef(endDate);

    startDateRef.current = startDate;
    endDateRef.current = endDate;

    const {
      Modal: MobileModal,
      openModal: openMobileModal,
      closeModal: closeMobileModal,
      isOpen: isMobileModalOpened,
    } = useModal({ resetStyles: true });

    const handleSelect = useCallback(
      ({ close }: IControlProps) =>
        () => {
          const currentStartDate = startDateRef.current;
          const currentEndDate = endDateRef.current;

          const newFrom = currentStartDate ? format(currentStartDate, DateFormatsEnum.Start) : null;
          const newTo = (() => {
            if (currentEndDate) {
              return format(currentEndDate, DateFormatsEnum.End);
            }
            if (currentStartDate) {
              return format(currentStartDate, DateFormatsEnum.End);
            }
            return null;
          })();

          onSelect?.({
            from: newFrom,
            to: newTo,
          });

          close();
        },
      [onSelect],
    );

    const handleReset = useCallback(() => {
      setStartDate(null);
      setEndDate(null);
      onReset?.();
    }, [onReset]);

    useImperativeHandle(
      ref,
      () => ({
        resetDate: handleReset,
        click: () => {
          buttonRef.current?.click();
        },
        resetFilters: noop,
        resetSearch: noop,
      }),
      [handleReset],
    );

    useEffect(() => {
      if (startDate && endDate && isBefore(endDate, startDate)) {
        setEndDate(null);
      }
    }, [startDate, endDate]);

    const handleOpen = useCallback(() => {
      if (from) {
        setStartDate(getUTCDate(from));
      }
      if (to) {
        setEndDate(getUTCDate(to));
      }
    }, [from, to]);

    const handleClose = useCallback(() => {
      const currentStartDate = startDateRef.current;
      const currentEndDate = endDateRef.current;
      if (!currentStartDate && !currentEndDate) {
        onSelect?.({ from: null, to: null });
      }
    }, [onSelect]);

    const isStartDate = useCallback(
      (d: Date) => (startDate && isSameDay(startDate, d) ? styles.start : null),
      [startDate],
    );

    const handleModalOpen = useCallback(() => {
      handleOpen();
      openMobileModal();
    }, [handleOpen, openMobileModal]);

    useEffect(() => {
      if (wide && isMobileModalOpened) {
        closeMobileModal();
      }
    }, [wide, closeMobileModal, isMobileModalOpened]);

    const dayCount = useMemo(() => {
      if (startDate && endDate) {
        return differenceInDays(endDate, startDate) + 1;
      }
      if (startDate) {
        return 1;
      }
      return 0;
    }, [startDate, endDate]);

    const label = useMemo(() => {
      if (from || to) {
        const fromLabel = from ? format(getUTCDate(from), 'dd MMM yyyy') : 'Select';
        const toLabel = to ? format(getUTCDate(to), 'dd MMM yyyy') : 'Select';

        return `${fromLabel} - ${toLabel}`;
      }
      return 'Select date range';
    }, [from, to]);

    const renderControls = useCallback(
      ({ close }: IControlProps) => (
        <div className={styles.controls}>
          <div className={styles.rangeCount}>
            {dayCount} day{dayCount === 1 ? '' : 's'}
          </div>
          <ActionButton
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            outlined
            onClick={handleReset}
            dataAction="date_picker_reset"
          >
            Reset
          </ActionButton>
          <ActionButton
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            onClick={handleSelect({ close })}
            dataAction="date_picker_select"
          >
            Select
          </ActionButton>
        </div>
      ),
      [handleReset, handleSelect, dayCount],
    );

    const shrinkLayout = useMemo(
      () => (
        <MobileModal
          className={cn({ [styles.aboveDialogModal]: aboveDialog })}
          overlayClassName={cn({ [styles.aboveDialogOverlay]: aboveDialog })}
        >
          {() => (
            <div className={cn(styles.picker, styles.mobile)}>
              <div className={cn(styles.calendarsPanel)}>
                <DatePicker
                  openToDate={startDate || undefined}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={([start, end]: [Date, Date]) => {
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  selectsRange
                  maxDate={maximumDate}
                  inline
                  disabledKeyboardNavigation
                  dayClassName={isStartDate}
                />
              </div>
              {renderControls({ close: closeMobileModal })}
            </div>
          )}
        </MobileModal>
      ),
      [MobileModal, startDate, endDate, renderControls, closeMobileModal, isStartDate, maxDate, aboveDialog],
    );

    const wideLayout = useMemo(() => {
      return (
        <>
          <DatePicker
            selectsStart
            openToDate={startDate || undefined}
            startDate={startDate}
            endDate={endDate}
            onChange={(newStartDate) => setStartDate(Array.isArray(newStartDate) ? newStartDate[0] : newStartDate)}
            maxDate={maximumDate}
            inline
            disabledKeyboardNavigation
            dayClassName={isStartDate}
          />
          <DatePicker
            disabledKeyboardNavigation
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            openToDate={endDate || undefined}
            onChange={(newEndDate) => setEndDate(Array.isArray(newEndDate) ? newEndDate[1] : newEndDate)}
            maxDate={maximumDate}
            minDate={Array.isArray(startDate) ? startDate[0] : startDate}
            inline
          />
        </>
      );
    }, [startDate, endDate, isStartDate, maxDate]);

    return wide ? (
      <DropdownWithContent
        onOpen={handleOpen}
        onClose={handleClose}
        contentClassName={cn(styles.container, { [styles.upward]: openUpward })}
        labelClassName={labelClassName}
        label={({ isOpened }) => (
          <ControlButton ref={buttonRef} label={label} isOpened={isOpened} muiArrow={muiArrow} filterStyle />
        )}
        dataAction="date_picker"
      >
        {({ close }) => (
          <div className={cn(styles.picker, { [styles.pickerUpward]: openUpward }, pickerClassName)}>
            <div className={cn(styles.calendarsPanel)}>{wideLayout}</div>
            {renderControls({ close })}
          </div>
        )}
      </DropdownWithContent>
    ) : (
      <div className={labelClassName}>
        <ControlButton
          ref={buttonRef}
          label={label}
          isOpened={isMobileModalOpened}
          onClick={handleModalOpen}
          filterStyle
        />
        {shrinkLayout}
      </div>
    );
  },
);

export default DateRangePicker;
